import React, { useEffect, useRef, useState } from 'react';
import { gql, useMutation } from '@apollo/client';
import { Box, Button, TextField, Typography } from '@mui/material';

const REQUEST = gql`mutation RequestBookingEmailCode($email: String!) { requestBookingEmailCode(email: $email) }`;
const VERIFY = gql`mutation VerifyBookingEmailCode($email: String!, $code: String!) { verifyBookingEmailCode(email: $email, code: $code) }`;

// Mount with key=email so editing the address starts a fresh verification.
export default function BookingEmailVerification({ email, proof, onVerified, disabled }) {
  const [request] = useMutation(REQUEST);
  const [verify] = useMutation(VERIFY);
  const [code, setCode] = useState('');
  const [sent, setSent] = useState(false);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState('Verify your email to confirm this booking.');
  const mounted = useRef(true);
  useEffect(() => { mounted.current = true; return () => { mounted.current = false; }; }, []);
  const valid = email.length <= 254 && /^[^\s@<>]+@[^\s@<>]+\.[^\s@<>]+$/.test(email);
  const submit = async (check) => {
    setBusy(true);
    try {
      const result = await (check ? verify : request)({ variables: { email, ...(check ? { code } : {}) } });
      if (!mounted.current) return;
      if (check) onVerified({ email, token: result.data.verifyBookingEmailCode });
      else { setSent(true); setNotice('Enter the code sent to your email. It expires in 10 minutes.'); }
    } catch (error) {
      if (mounted.current) setNotice(error.message || 'Could not verify email. Try again.');
    } finally { if (mounted.current) setBusy(false); }
  };
  return <Box sx={{ my: 2 }}>
    <Typography variant="body2" role="status" color={proof ? 'success.main' : 'text.secondary'}>{proof ? 'Email verified.' : notice}</Typography>
    {!proof && <Box sx={{ mt: 1, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
      <Button type="button" disabled={!valid || busy || disabled} onClick={() => submit(false)}>{sent ? 'Resend code' : 'Verify email'}</Button>
      {sent && <>
        <TextField size="small" label="Verification code" value={code} onChange={e => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))} inputProps={{ inputMode: 'numeric', autoComplete: 'one-time-code', maxLength: 6 }} />
        <Button type="button" disabled={code.length !== 6 || busy || disabled} onClick={() => submit(true)}>Confirm code</Button>
      </>}
    </Box>}
  </Box>;
}
